import { useState } from 'react';
import { faDatabase } from '@fortawesome/pro-solid-svg-icons';

import { ModalProps } from '@typings';
import { deleteDisk } from '@services';
import { clusterContextSelector, contextNamesSelector } from '@selectors';
import { useSelector } from '@hooks';
import { invariant, noop, toastifyResponseError } from '@utils';

import { Button, Icon, Modal } from '@components';

type Props = ModalProps & {
  id: string;
  name: string;
  getDisks: () => Promise<void>;
};

export const DeleteDiskModal = ({
  id,
  name,
  getDisks,
  closeModal = noop,
}: Props) => {
  const { organizationName, projectName } = useSelector(contextNamesSelector);
  const { disksUrl } = useSelector(clusterContextSelector)!;

  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);

    try {
      invariant(organizationName);
      invariant(projectName);

      await deleteDisk({ disksUrl, organizationName, projectName, id });
      await getDisks();

      closeModal();
    } catch (error) {
      toastifyResponseError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal.Content title="Delete Disk" className="w-[520px]">
      <p>
        Are you sure you want to delete
        <span className="space-both inline items-center gap-1 text-body font-700 text-black">
          <Icon
            icon={faDatabase}
            className="mr-1 inline h-4 w-4 text-[14px] text-success"
          />
          {name}
        </span>
        disk?
      </p>

      <Modal.Footer>
        <Button variant="ghost" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant="error"
          loading={loading}
          className="px-10"
          onClick={handleDelete}
        >
          Delete
        </Button>
      </Modal.Footer>
    </Modal.Content>
  );
};
